import { SdkContext } from '../internal'
import type { DayPassAdjustInput, DayPassEntry, DayPassReport, DayPassWallet, DayPassWalletPage } from '@/types/dayPasses'

type WalletEnvelope = { data?: DayPassWallet; pagination?: DayPassWalletPage['pagination'] }

const toPage = (body: WalletEnvelope): DayPassWalletPage => {
  const wallet = body?.data
  if (!wallet) throw new Error('dayPasses: empty answer')
  return { wallet, pagination: body.pagination ?? null }
}

/**
 * F-249: the day passes of the Attendance ("Pacote de passes"). Paths are written by hand (F-03);
 * the community is the header, never the path. The wallet answers carry `pagination` for the history
 * beside an object `data`, so they are read through the envelope.
 */
export class DayPassesResource {
  constructor(private ctx: SdkContext) {}

  /** The caller's own passes in this community: balance, valid packs, history and packs on sale. */
  async mine(page = 1, limit = 20): Promise<DayPassWalletPage> {
    return toPage(await this.ctx.envelope<WalletEnvelope>({ httpMethod: 'GET', path: '/day-passes/me' }, { query: { page, limit } }))
  }

  /** A member's passes: the team reads anyone's, a member only their own (403). */
  async ofMember(memberId: string, page = 1, limit = 20): Promise<DayPassWalletPage> {
    return toPage(
      await this.ctx.envelope<WalletEnvelope>({ httpMethod: 'GET', path: '/day-passes/members/{memberId}' }, { memberId, query: { page, limit } })
    )
  }

  /** Owner, admin and finance grant (positive) or take (negative) passes, with a reason. */
  async adjust(memberId: string, body: DayPassAdjustInput): Promise<DayPassWallet & { entries: DayPassEntry[] }> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/day-passes/members/{memberId}/adjustments', hasBody: true },
      { memberId, body }
    )
  }

  /** The team's period report (days in the community's zone, `from` and `to` inclusive). */
  async report(range: { from?: string; to?: string } = {}): Promise<DayPassReport> {
    return this.ctx.call({ httpMethod: 'GET', path: '/day-passes/report' }, { query: range })
  }
}
